import React, { useEffect, useState } from "react";
import axios from "../../../axios";
import { useToast } from "@chakra-ui/react";

const Toggle = ({ checked, onChange }) => (
  <label className="relative inline-flex items-center cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="sr-only"
    />
    <div
      className="w-11 h-6 rounded-full transition-colors"
      style={{ backgroundColor: checked ? '#22c55e' : '#e5e7eb' }}
    >
      <div
        className="absolute top-[2px] h-5 w-5 bg-white border border-gray-300 rounded-full transition-transform shadow-sm"
        style={{ left: '2px', transform: checked ? 'translateX(20px)' : 'translateX(0)' }}
      />
    </div>
  </label>
);

const ReferralConfig = () => {
  const toast = useToast({ position: "top" });
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get("/admin/referral/config");
        if (res.data?.result) {
          setConfig(res.data.result);
        }
      } catch (error) {
        console.error("Error fetching config", error);
        toast({ title: "Failed to load config", status: "error", duration: 3000 });
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.patch("/admin/referral/config", {
        isActive: config.isActive,
        signupReward: config.signupReward,
        firstRechargeReward: config.firstRechargeReward,
        planPurchaseReward: config.planPurchaseReward,
        referredBonus: config.referredBonus,
        referralCodeLength: config.referralCodeLength,
        maxRewardsPerUser: config.maxRewardsPerUser,
        minPlanAmountForReward: config.minPlanAmountForReward,
      });
      if (res.data?.result) {
        setConfig(res.data.result);
      }
      toast({ title: "Config saved successfully", status: "success", duration: 3000 });
    } catch (error) {
      toast({ title: "Failed to save config", status: "error", duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-bgBlue"></div>
      </div>
    );
  }

  if (!config) {
    return <div className="p-6 text-center text-gray-500">Failed to load configuration</div>;
  }

  return (
    <div className="p-4 lg:p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-bgBlue">Referral Settings</h1>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-bgBlue text-white rounded-lg font-semibold disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Settings */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-bold text-bgBlue mb-4">General</h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-bgBlue">Referral Program Active</p>
                <p className="text-xs text-gray-400">Enable or disable the entire referral system</p>
              </div>
              <Toggle
                checked={config.isActive}
                onChange={(e) => setConfig({ ...config, isActive: e.target.checked })}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-bgBlue mb-1">
                Referral Code Length
              </label>
              <input
                type="number"
                value={config.referralCodeLength}
                onChange={(e) => setConfig({ ...config, referralCodeLength: parseInt(e.target.value) || 8 })}
                min={4}
                max={16}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bgBlue"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-bgBlue mb-1">
                Max Rewards Per User
              </label>
              <input
                type="number"
                value={config.maxRewardsPerUser}
                onChange={(e) => setConfig({ ...config, maxRewardsPerUser: parseInt(e.target.value) || 0 })}
                min={0}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bgBlue"
              />
              <p className="text-xs text-gray-400 mt-1">0 = unlimited referrals</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-bgBlue mb-1">
                Min Plan Amount for Reward (coins)
              </label>
              <input
                type="number"
                value={config.minPlanAmountForReward}
                onChange={(e) => setConfig({ ...config, minPlanAmountForReward: parseInt(e.target.value) || 0 })}
                min={0}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bgBlue"
              />
              <p className="text-xs text-gray-400 mt-1">0 = no minimum required</p>
            </div>
          </div>
        </div>

        {/* Signup Reward */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-bold text-bgBlue mb-4">1. Signup Reward</h2>
          <p className="text-xs text-gray-400 mb-4">Reward when referred friend installs the app</p>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-bgBlue">Enable Signup Reward</p>
                <p className="text-xs text-gray-400">Credit referrer when friend signs up</p>
              </div>
              <Toggle
                checked={config.signupReward.enabled}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    signupReward: { ...config.signupReward, enabled: e.target.checked },
                  })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-bgBlue mb-1">
                Reward Amount (coins)
              </label>
              <input
                type="number"
                value={config.signupReward.amount}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    signupReward: { ...config.signupReward, amount: parseInt(e.target.value) || 0 },
                  })
                }
                min={0}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bgBlue"
              />
            </div>
          </div>
        </div>

        {/* First Recharge Reward */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-bold text-bgBlue mb-4">2. First Recharge Reward</h2>
          <p className="text-xs text-gray-400 mb-4">Reward when referred friend completes first wallet recharge</p>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-bgBlue">Enable First Recharge Reward</p>
                <p className="text-xs text-gray-400">Credit referrer when friend recharges wallet</p>
              </div>
              <Toggle
                checked={config.firstRechargeReward.enabled}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    firstRechargeReward: { ...config.firstRechargeReward, enabled: e.target.checked },
                  })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-bgBlue mb-1">
                Reward Amount (coins)
              </label>
              <input
                type="number"
                value={config.firstRechargeReward.amount}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    firstRechargeReward: { ...config.firstRechargeReward, amount: parseInt(e.target.value) || 0 },
                  })
                }
                min={0}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bgBlue"
              />
            </div>
          </div>
        </div>

        {/* Plan Purchase Reward */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-bold text-bgBlue mb-4">3. Plan Purchase Reward</h2>
          <p className="text-xs text-gray-400 mb-4">Reward when referred friend purchases a plan</p>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-bgBlue">Enable Plan Purchase Reward</p>
                <p className="text-xs text-gray-400">Credit referrer when friend buys a plan</p>
              </div>
              <Toggle
                checked={config.planPurchaseReward.enabled}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    planPurchaseReward: { ...config.planPurchaseReward, enabled: e.target.checked },
                  })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-bgBlue mb-1">Reward Type</label>
              <select
                value={config.planPurchaseReward.rewardType}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    planPurchaseReward: { ...config.planPurchaseReward, rewardType: e.target.value },
                  })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bgBlue"
              >
                <option value="fixed">Fixed Amount (coins)</option>
                <option value="percentage">Percentage of Plan Price</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-bgBlue mb-1">
                {config.planPurchaseReward.rewardType === "fixed" ? "Reward Amount (coins)" : "Reward Percentage (%)"}
              </label>
              <input
                type="number"
                value={config.planPurchaseReward.amount}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    planPurchaseReward: { ...config.planPurchaseReward, amount: parseInt(e.target.value) || 0 },
                  })
                }
                min={0}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bgBlue"
              />
            </div>
          </div>
        </div>

        {/* Referred User Bonus */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-bold text-bgBlue mb-4">Signup Bonus (for referred user)</h2>
          <p className="text-xs text-gray-400 mb-4">Bonus given to the new user who uses a referral code</p>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-bgBlue">Enable Signup Bonus</p>
                <p className="text-xs text-gray-400">Give bonus coins to referred users</p>
              </div>
              <Toggle
                checked={config.referredBonus.enabled}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    referredBonus: { ...config.referredBonus, enabled: e.target.checked },
                  })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-bgBlue mb-1">
                Bonus Amount (coins)
              </label>
              <input
                type="number"
                value={config.referredBonus.amount}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    referredBonus: { ...config.referredBonus, amount: parseInt(e.target.value) || 0 },
                  })
                }
                min={0}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bgBlue"
              />
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-white rounded-xl p-6 shadow-sm lg:col-span-2">
          <h2 className="text-lg font-bold text-bgBlue mb-4">Current Configuration Preview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Status</span>
              <span className={`font-bold ${config.isActive ? "text-green-600" : "text-red-600"}`}>
                {config.isActive ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">On Signup</span>
              <span className="font-bold text-bgBlue">
                {config.signupReward.enabled ? `+${config.signupReward.amount} coins` : "Disabled"}
              </span>
            </div>
            <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">On First Recharge</span>
              <span className="font-bold text-bgBlue">
                {config.firstRechargeReward.enabled ? `+${config.firstRechargeReward.amount} coins` : "Disabled"}
              </span>
            </div>
            <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">On Plan Purchase</span>
              <span className="font-bold text-bgBlue">
                {config.planPurchaseReward.enabled
                  ? config.planPurchaseReward.rewardType === "fixed"
                    ? `+${config.planPurchaseReward.amount} coins`
                    : `+${config.planPurchaseReward.amount}%`
                  : "Disabled"}
              </span>
            </div>
            <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">New user gets</span>
              <span className="font-bold text-bgBlue">
                {config.referredBonus.enabled ? `+${config.referredBonus.amount} coins` : "No bonus"}
              </span>
            </div>
            <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Max referrals per user</span>
              <span className="font-bold text-bgBlue">
                {config.maxRewardsPerUser === 0 ? "Unlimited" : config.maxRewardsPerUser}
              </span>
            </div>
          </div>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-bgBlue font-semibold">Total possible earnings per referral:</p>
            <p className="text-2xl font-bold text-bgBlue">
              {(() => {
                let total = 0;
                if (config.signupReward.enabled) total += config.signupReward.amount;
                if (config.firstRechargeReward.enabled) total += config.firstRechargeReward.amount;
                if (config.planPurchaseReward.enabled && config.planPurchaseReward.rewardType === "fixed") total += config.planPurchaseReward.amount;
                return `${total} coins`;
              })()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralConfig;
